import React, { useEffect, useRef, useState } from 'react';
import { useCms } from '@/context/CmsContext';
import { fetchPageContent, uploadMedia } from '@/services/cms';
import styles from './cms.module.css';

// Documents that admins can replace from the Files panel.
// Each entry maps a CMS field to a human-friendly label + accepted mimetypes.
// Add new rows here to expose more managed documents.
export const MANAGED_FILES: ManagedFile[] = [
    {
        page: 'giveaway',
        section: 'rules',
        field: 'pdf_url',
        label: 'Official Giveaway Rules',
        description: 'PDF linked from the /win page and the giveaway entry form.',
        accept: 'application/pdf',
    },
    {
        page: 'giveaway',
        section: 'image',
        field: 'url',
        label: 'Giveaway Graphic (/win)',
        description: 'Image on the /win page. Leave empty to mirror the homepage offers graphic; upload here to override it.',
        accept: 'image/*',
    },
];

interface ManagedFile {
    page: string;
    section: string;
    field: string;
    label: string;
    description: string;
    accept: string;
}

interface FilesPanelProps {
    onClose: () => void;
}

export default function FilesPanel({ onClose }: FilesPanelProps) {
    return (
        <div className={styles.historyPanel}>
            <div className={styles.historyHeader}>
                <span className={styles.historyTitle}>Files</span>
                <button onClick={onClose} className={styles.historyClose}>&times;</button>
            </div>

            <div className={styles.historyResults}>
                {MANAGED_FILES.map((f) => (
                    <FileRow key={`${f.page}.${f.section}.${f.field}`} file={f} />
                ))}
            </div>
        </div>
    );
}

function FileRow({ file }: { file: ManagedFile }) {
    const { trackChange, getPendingValue } = useCms();
    const [currentUrl, setCurrentUrl] = useState<string>('');
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        fetchPageContent(file.page, file.section)
            .then((content) => {
                const v = content?.[file.section]?.[file.field];
                if (v) setCurrentUrl(v);
            })
            .catch(() => {});
    }, [file.page, file.section, file.field]);

    const pending = getPendingValue(file.page, file.section, file.field);
    const displayUrl = pending !== undefined ? pending : currentUrl;
    const filenameFromUrl = displayUrl ? displayUrl.split('/').pop() : '';

    const handleFile = async (selected: File) => {
        setUploading(true);
        setError('');
        try {
            const media = await uploadMedia(selected, file.label);
            const newUrl = media.url || media.r2_key;
            trackChange(file.page, file.section, file.field, newUrl, currentUrl, 'url');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Upload failed.');
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = '';
        }
    };

    return (
        <div className={styles.historyItem}>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div className={styles.historyTimestamp}>
                    {file.label}
                    {pending !== undefined && (
                        <span style={{ marginLeft: 8, color: '#f59e0b' }}>· pending</span>
                    )}
                </div>
                <div className={styles.historyValue}>
                    {file.description}
                </div>
                {displayUrl && (
                    <a
                        href={displayUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{ display: 'block', marginTop: 4, fontSize: 11, color: '#60a5fa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                        {filenameFromUrl}
                    </a>
                )}
                {error && (
                    <div style={{ color: '#ef4444', fontSize: 11, marginTop: 4 }}>{error}</div>
                )}
            </div>
            <input
                ref={inputRef}
                type="file"
                accept={file.accept}
                style={{ display: 'none' }}
                onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFile(f);
                }}
            />
            <button
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className={styles.historyRestoreBtn}
            >
                {uploading ? '...' : 'Replace'}
            </button>
        </div>
    );
}
